import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../../Common/Modal/Modal';
import { InputText } from 'primereact/inputtext';
import Toggle from '@Components/Common/Toggle/Toggle';
import { useSelector } from 'react-redux';

interface JobTitle {
	id: number;
	name: string;
	isActive: boolean;
	subscriptionId: number;
}

interface AddJobTitleProps {
	onSave: (jobTitle: JobTitle) => void;
	onCancel: () => void;
}

const AddJobTitle: React.FC<AddJobTitleProps> = ({ onSave, onCancel }) => {
	// Memoize subscription ID to prevent unnecessary re-renders
	const currentSubscriptionId = useSelector((state: { user: { currentSubscriptionId: number } }) => state.user.currentSubscriptionId);

	const initialJobTitle = useMemo(
		() => ({
			id: 0,
			name: '',
			isActive: true,
			subscriptionId: currentSubscriptionId || 0,
			disciplineId: 0,
		}),
		[currentSubscriptionId]
	);

	const [newJobTitle, setNewJobTitle] = useState<JobTitle>(initialJobTitle);
	const isInputInvalid = !newJobTitle.name.trim(); // Dynamically determine invalid state

	// Memoized change handlers
	const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setNewJobTitle((prev) => (prev.name === value ? prev : { ...prev, name: value }));
	}, []);

	const handleToggleChange = useCallback((e: { target: { checked: boolean } }) => {
		setNewJobTitle((prev) => (prev.isActive === e.target.checked ? prev : { ...prev, isActive: e.target.checked }));
	}, []);

	const handleSave = useCallback(() => {
		if (isInputInvalid) return;
		onSave(newJobTitle);
	}, [isInputInvalid, onSave, newJobTitle]);

	return (
		<Modal
			title='Add JobTitle'
			body={
				<>
					<InputText
						id='name'
						name='name'
						placeholder='Name'
						invalid={isInputInvalid}
						type='text'
						value={newJobTitle.name}
						onChange={handleOnChange}
						maxLength={175}
						className='mb-3'
					/>
					<Toggle name='isActive' text='Active' checked={newJobTitle.isActive} onChange={handleToggleChange} />
				</>
			}
			primaryButtonText='Add'
			onPrimaryClick={handleSave}
			onSecondaryClick={onCancel}
			onCloseClick={onCancel}
		/>
	);
};

AddJobTitle.propTypes = {
	onSave: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default AddJobTitle;
